// Schedules.js
import { useState, useEffect } from 'react';
import { schedulesAPI, tenantsAPI } from '../services/api';
import { Calendar, Plus, Edit2, Trash2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

const SCHEDULE_TYPES = ['default', 'once_off', 'recurring', 'seasonal', 'emergency'];
const TYPE_COLORS = { default: 'grey', once_off: 'purple', recurring: 'green', seasonal: 'yellow', emergency: 'red' };

function ScheduleModal({ onClose, onSaved, tenants }) {
  const [form, setForm] = useState({ name: '', schedule_type: 'default', scope: 'brand', tenant_id: '' });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await schedulesAPI.create({ ...form, tenant_id: form.tenant_id || null });
      toast.success('Schedule created');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">New Schedule</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input className="form-input" value={form.name} required onChange={e => set('name', e.target.value)} placeholder="e.g. Christmas Campaign" />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-select" value={form.schedule_type} onChange={e => set('schedule_type', e.target.value)}>
                {SCHEDULE_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Scope</label>
              <select className="form-select" value={form.scope} onChange={e => set('scope', e.target.value)}>
                <option value="platform">Platform</option>
                <option value="brand">Brand</option>
                <option value="region">Region</option>
                <option value="store">Store</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Brand</label>
            <select className="form-select" value={form.tenant_id} onChange={e => set('tenant_id', e.target.value)}>
              <option value="">All brands (platform)</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {form.schedule_type === 'once_off' && (
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Start</label>
                <input type="datetime-local" className="form-input" onChange={e => set('once_off_start', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">End</label>
                <input type="datetime-local" className="form-input" onChange={e => set('once_off_end', e.target.value)} />
              </div>
            </div>
          )}
          {form.schedule_type === 'recurring' && (
            <div className="form-group">
              <label className="form-label">Days</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['mon','tue','wed','thu','fri','sat','sun'].map(d => (
                  <label key={d} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20, background: (form.recurring_days||[]).includes(d) ? 'var(--accent-glow)' : 'var(--bg-elevated)', border: `1px solid ${(form.recurring_days||[]).includes(d) ? 'rgba(108,99,255,0.4)' : 'var(--border)'}`, cursor: 'pointer', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                    <input type="checkbox" checked={(form.recurring_days||[]).includes(d)} onChange={() => {
                      const days = form.recurring_days || [];
                      set('recurring_days', days.includes(d) ? days.filter(x => x !== d) : [...days, d]);
                    }} style={{ display: 'none' }} />
                    {d}
                  </label>
                ))}
              </div>
            </div>
          )}
          {form.schedule_type === 'emergency' && (
            <div className="form-group">
              <label className="form-label">Emergency Message</label>
              <textarea className="form-textarea" value={form.emergency_message||''} onChange={e => set('emergency_message', e.target.value)} placeholder="Describe the emergency override..." />
            </div>
          )}
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Creating...' : 'Create Schedule'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function Schedules() {
  const [schedules, setSchedules] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([schedulesAPI.list(), tenantsAPI.list()])
      .then(([s, t]) => { setSchedules(s.data); setTenants(t.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const del = async (id) => {
    if (!window.confirm('Delete schedule?')) return;
    try { await schedulesAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Schedules</h1><p className="page-subtitle">Manage programming schedules and time-based overrides</p></div>
        <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={16} /> New Schedule</button>
      </div>
      <div className="card">
        {loading ? <div className="loading-state"><div className="spinner" /></div> : schedules.length === 0 ? (
          <div className="empty-state"><Calendar size={48} /><h3>No schedules</h3><p>Create your first schedule to control store programming</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Name</th><th>Type</th><th>Scope</th><th>Brand</th><th>Priority</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {schedules.map(s => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 600 }}>
                      {s.schedule_type === 'emergency' && <AlertTriangle size={14} color="var(--red)" style={{ marginRight: 6 }} />}
                      {s.name}
                    </td>
                    <td><span className={`badge badge-${TYPE_COLORS[s.schedule_type]}`}>{s.schedule_type.replace('_',' ')}</span></td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{s.scope}</td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{tenants.find(t => t.id === s.tenant_id)?.name || 'Platform'}</td>
                    <td><span className="mono" style={{ fontSize: 13 }}>{s.priority}</span></td>
                    <td><span className={`badge badge-${s.is_active ? 'green' : 'grey'}`}>{s.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td><button className="btn btn-danger btn-sm" onClick={() => del(s.id)}><Trash2 size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {modal && <ScheduleModal tenants={tenants} onClose={() => setModal(false)} onSaved={() => { setModal(false); load(); }} />}
    </div>
  );
}

export default Schedules;
